import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Card from '../../components/Card';
import Modal from '../../components/Modal';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { api } from '../../shared/api';
import { useVehicles, useCreateVehicle, useUpdateVehicle, useDeleteVehicle } from '../../entities/vehicle';
import { useCurrency } from '../../shared/CurrencyContext';
import { readListJsSort, attachListJsSortPersistence } from '../../shared/useSortPreference';
import List from 'list.js';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../shared/permissions';
import Select from '../../components/form/AppSelect';
import { Link } from 'react-router-dom';
import { Alert, InputGroup } from 'react-bootstrap';
import { IconSearch } from '@tabler/icons-react';
import { useConfirm } from '../../shared/ConfirmContext';
import '../../shared/ReactSelectDarkMode.css';
import VehicleTariffManagement from './VehicleTariffManagement';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import StatusBadge from '../../components/ui/StatusBadge';
import EmptyState from '../../components/feedback/EmptyState';

// Custom theme for React Select to support dark mode
const selectTheme = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'var(--tblr-primary)',
    primary75: 'var(--tblr-primary-rgb)',
    primary50: 'var(--tblr-primary-rgb)',
    primary25: 'var(--tblr-primary-rgb)',
    danger: 'var(--tblr-danger)',
    dangerLight: 'var(--tblr-danger-rgb)',
    neutral0: 'var(--tblr-bg-forms)',
    neutral5: 'var(--tblr-bg-surface)',
    neutral10: 'var(--tblr-bg-surface)',
    neutral20: 'var(--tblr-border-color)',
    neutral30: 'var(--tblr-border-color)',
    neutral40: 'var(--tblr-body-color-secondary)',
    neutral50: 'var(--tblr-body-color-secondary)',
    neutral60: 'var(--tblr-body-color-secondary)',
    neutral70: 'var(--tblr-body-color-secondary)',
    neutral80: 'var(--tblr-body-color)',
    neutral90: 'var(--tblr-body-color)'
  }
});

function Transport() {
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const { canManage } = usePermissions();
  const { formatCurrency } = useCurrency();

  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState(null);
  const [vehicleToEdit, setVehicleToEdit] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const { data: vehicles = [], isLoading, error: queryError } = useVehicles();

  const { data: categories = [] } = useQuery({
    queryKey: ['transport', 'categories'],
    queryFn: () => api.get('transport/api/categories/'),
    select: (data) => data || [],
  });

  const categoryOptions = useMemo(
    () => categories.map(c => ({ value: c.id, label: c.name })),
    [categories]
  );

  const [newVehicle, setNewVehicle] = useState({
    name: '',
    category: '',
    description: '',
    licence_plate: '',
    available: true,
    next_technical_check_date: '',
    km_tariff_amount: '',
    flat_tariff_amount: '',
  });

  const listInstance = useRef(null);
  const tableRef = useRef(null);

  // Initialize List.js when component mounts and data is available
  useEffect(() => {
    let cleanupSort = () => {};

    if (!isLoading && vehicles.length > 0) {
      // Destroy existing List.js instance
      if (listInstance.current) {
        try {
          if (typeof listInstance.current.destroy === 'function') {
            listInstance.current.destroy();
          } else {
            listInstance.current = null;
          }
        } catch (error) {
          console.warn('Error cleaning up list.js instance:', error);
          listInstance.current = null;
        }
      }

      // Initialize new List.js instance using the element ID
      try {
        listInstance.current = new List('table-default', {
          sortClass: 'table-sort',
          listClass: 'table-tbody',
          valueNames: ['name', 'category_name', 'licence_plate', 'km_rate', 'flat_rate', 'available_status', 'technical_check_status']
        });

        // Restore saved sort state
        const saved = readListJsSort('transport');
        if (saved?.col) {
          listInstance.current.sort(saved.col, { order: saved.order || 'asc' });
        }

        // Persist sort state on each click
        cleanupSort = attachListJsSortPersistence('transport', tableRef);
      } catch (error) {
        console.warn('Error initializing list.js:', error);
      }
    }

    return () => {
      cleanupSort();
      if (listInstance.current) { // eslint-disable-line
        try {
          if (typeof listInstance.current.destroy === 'function') {
            listInstance.current.destroy();
          } else {
            listInstance.current = null;
          }
        } catch (error) {
          console.warn('Error cleaning up list.js instance:', error);
          listInstance.current = null;
        }
      }
    };
  }, [isLoading, vehicles]);

  const handleSearchInput = (e) => {
    const searchTerm = e.target.value;
    if (listInstance.current) {
      listInstance.current.search(searchTerm);
    }
  };

  const createVehicle = useCreateVehicle();
  const createMutation = {
    isPending: createVehicle.isPending,
    mutate: (vehicle) => createVehicle.mutate(vehicle, {
      onSuccess: () => { resetForm(); setError(null); setFormErrors(null); },
      onError: (err) => setFormErrors(err.response?.data || { error: err.message }),
    }),
  };

  const updateVehicle = useUpdateVehicle();
  const updateMutation = {
    isPending: updateVehicle.isPending,
    mutate: ({ id, ...data }) => updateVehicle.mutate({ id, data }, {
      onSuccess: () => { resetForm(); setError(null); setFormErrors(null); },
      onError: (err) => setFormErrors(err.response?.data || { error: err.message }),
    }),
  };

  const deleteVehicle = useDeleteVehicle();
  const deleteMutation = {
    isPending: deleteVehicle.isPending,
    mutate: (id) => deleteVehicle.mutate(id, {
      onError: (err) => setError(err.message || t('transport.errors.failedToDelete')),
    }),
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (vehicleToEdit) {
      updateMutation.mutate({ id: vehicleToEdit.id, ...newVehicle });
    } else {
      createMutation.mutate(newVehicle);
    }
  };

  const handleEdit = (vehicle) => {
    try {
            setVehicleToEdit(vehicle);
      setNewVehicle({
        name: vehicle.name || '',
        category: vehicle.category || '',
        description: vehicle.description || '',
        licence_plate: vehicle.licence_plate || '',
        available: vehicle.available !== undefined ? vehicle.available : true,
        next_technical_check_date: vehicle.next_technical_check_date || '',
        km_tariff_amount: vehicle.km_tariff_amount ?? '',
        flat_tariff_amount: vehicle.flat_tariff_amount ?? '',
      });
      setFormErrors(null);
      setShowModal(true);
    } catch (err) {
      console.error('Error in handleEdit:', err);
      setError(t('transport.errors.failedToEdit'));
    }
  };

  const handleDelete = async (vehicleId) => {
    if (await confirm(t('transport.confirmDelete'))) {
      deleteMutation.mutate(vehicleId);
    }
  };

  const resetForm = () => {
    setNewVehicle({
      name: '',
      category: '',
      description: '',
      licence_plate: '',
      available: true,
      next_technical_check_date: '',
      km_tariff_amount: '',
      flat_tariff_amount: '',
    });
    setVehicleToEdit(null);
    setFormErrors(null);
    setShowModal(false);
  };

  const getTechnicalCheckBadge = (vehicle) => {
    const status = vehicle.technical_check_status;
    const days = vehicle.days_until_technical_check;

    const badgeColors = {
      overdue: '#d63939',
      due_soon: '#f76707',
      ok: '#2fb344',
    };

    const labels = {
      overdue: t('transport.technicalCheck.overdue'),
      due_soon: `${t('transport.technicalCheck.dueSoon')} (${days} ${t('transport.days')})`,
      ok: `${t('transport.technicalCheck.ok')} (${days} ${t('transport.days')})`,
    };

    if (badgeColors[status]) {
      return <StatusBadge color={badgeColors[status]} label={labels[status]} />;
    }
    return <StatusBadge color="#6c757d" label={t('transport.technicalCheck.noDate')} />;
  };

  const pageError = queryError?.message || error;

  if (isLoading) {
    return (
      <PageShell pretitle={t('transport.subtitle')} title={t('transport.title')}>
        <PageSection>
          <CenteredLoader message={t('transport.loading')} />
        </PageSection>
      </PageShell>
    );
  }

  return (
    <>
      <PageShell
        pretitle={t('transport.subtitle')}
        title={t('transport.title')}
        description={t('transport.pageDescription')}
        actions={canManage ? (
          <>
            <button
              className="btn btn-primary d-none d-sm-inline-block"
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="icon me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M12 5l0 14" />
                <path d="M5 12l14 0" />
              </svg>
              {t('transport.addVehicle')}
            </button>
            <button
              className="btn btn-primary d-sm-none btn-icon"
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              aria-label={t('transport.addVehicle')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M12 5l0 14" />
                <path d="M5 12l14 0" />
              </svg>
            </button>
          </>
        ) : null}
      >
        {formErrors && (
          <Alert variant="danger" className="app-inline-alert" dismissible>
            {typeof formErrors === 'string'
              ? formErrors
              : Object.entries(formErrors).map(([key, value]) => (
                  <div key={key}><strong>{key}:</strong> {value}</div>
                ))}
          </Alert>
        )}

        {pageError && (
          <Alert variant="danger" className="app-inline-alert" dismissible>
            {pageError}
          </Alert>
        )}

        <PageSection
          title={t('transport.vehiclesList', { count: vehicles.length })}
          description={t('transport.sectionDescription', 'Vyhledávej vozidla podle názvu, kategorie nebo dostupnosti a otevři jejich detail bez přepínání kontextu.')}
          actions={
            <InputGroup className="app-search">
              <InputGroup.Text>
                <IconSearch size={16} />
              </InputGroup.Text>
              <input
                type="text"
                id="table-search"
                className="form-control table-search"
                placeholder={t('transport.searchPlaceholder')}
                onChange={handleSearchInput}
              />
            </InputGroup>
          }
          bodyClassName="p-0"
        >
                <div className="table-responsive">
                  {vehicles.length === 0 ? (
                    <EmptyState
                      title={t('transport.noVehiclesFound')}
                      message={t('transport.noVehiclesMessage')}
                      action={canManage ? (
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            resetForm();
                            setShowModal(true);
                          }}
                        >
                          {t('transport.addFirstVehicle')}
                        </button>
                      ) : null}
                    />
                  ) : (
                  <div id="table-default" ref={tableRef}>
                    <table className="table table-vcenter app-table mb-0">
                      <thead>
                        <tr>
                          <th><button className="table-sort" data-sort="name">{t('transport.fields.name')}</button></th>
                          <th><button className="table-sort" data-sort="category_name">{t('transport.fields.category')}</button></th>
                          <th><button className="table-sort" data-sort="licence_plate">{t('transport.fields.licencePlate')}</button></th>
                          <th className="text-end"><button className="table-sort" data-sort="km_rate">{t('transport.fields.kmRate')}</button></th>
                          <th className="text-end"><button className="table-sort" data-sort="flat_rate">{t('transport.fields.flatRate')}</button></th>
                          <th><button className="table-sort" data-sort="available_status">{t('transport.fields.status')}</button></th>
                          <th><button className="table-sort" data-sort="technical_check_status">{t('transport.fields.technicalCheck')}</button></th>
                          <th className="w-1">{t('common.actions')}</th>
                        </tr>
                      </thead>
                      <tbody className="table-tbody">
                        {vehicles.map((vehicle) => (
                          <tr key={vehicle.id}>
                            <td className="name">
                              <div className="d-flex py-1 align-items-center">
                                <div className="avatar avatar-sm me-3 bg-blue-lt">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24"
                                     viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none"
                                     strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                    <path d="M7 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"/>
                                    <path d="M17 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"/>
                                    <path d="M5 17h-2v-6l2 -5h9l4 5h1a2 2 0 0 1 2 2v4h-2m-4 0h-6m-6 -6h15m-6 0v-5"/>
                                  </svg>
                                </div>
                                <div className="flex-fill">
                                  <div className="font-weight-medium">
                                    <Link to={`/transport/${vehicle.id}`} className="text-reset">
                                      {vehicle.name}
                                    </Link>
                                  </div>
                                  <div className="text-muted">{vehicle.description ? vehicle.description.substring(0, 50) + (vehicle.description.length > 50 ? '...' : '') : t('common.noDescription')}</div>
                                </div>
                              </div>
                            </td>
                            <td className="category_name">
                              {vehicle.category_name ? (
                                <StatusBadge color={vehicle.category_color || '#6c757d'} label={vehicle.category_name} />
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td className="licence_plate">
                              <StatusBadge color="#6c757d" label={vehicle.licence_plate || t('common.notSet')} />
                            </td>
                            <td className="km_rate text-end">
                              {vehicle.km_tariff_amount != null && vehicle.km_tariff_amount !== '' ? (
                                <span className="fw-medium text-nowrap">
                                  {formatCurrency(vehicle.km_tariff_amount)}
                                  <span className="text-muted"> / km</span>
                                </span>
                              ) : (
                                <span className="text-muted">–</span>
                              )}
                            </td>
                            <td className="flat_rate text-end">
                              {vehicle.flat_tariff_amount != null && vehicle.flat_tariff_amount !== '' ? (
                                <span className="fw-medium text-nowrap">
                                  {formatCurrency(vehicle.flat_tariff_amount)}
                                  <span className="text-muted"> / {t('transport.fields.flatUnit')}</span>
                                </span>
                              ) : (
                                <span className="text-muted">–</span>
                              )}
                            </td>
                            <td className="available_status">
                              {vehicle.available ? (
                                <StatusBadge color="#2fb344" label={t('transport.status.available')} />
                              ) : (
                                <StatusBadge color="#d63939" label={t('transport.status.unavailable')} />
                              )}
                            </td>
                            <td className="technical_check_status">
                              {getTechnicalCheckBadge(vehicle)}
                            </td>
                            <td>
                              <div className="btn-group">
                                <Link
                                  to={`/transport/${vehicle.id}`}
                                  className="btn btn-outline-primary btn-sm"
                                  title={t('common.view')}
                                >
                                  <i className="ti ti-eye"></i>
                                </Link>
                                {canManage && (
                                  <>
                                    <button
                                      type="button"
                                      className="btn btn-outline-info btn-sm"
                                      onClick={() => handleEdit(vehicle)}
                                      title={t('common.edit')}
                                    >
                                      <i className="ti ti-edit"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={() => handleDelete(vehicle.id)}
                                      title={t('common.delete')}
                                    >
                                      <i className="ti ti-trash"></i>
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  )}
                </div>
        </PageSection>
      </PageShell>

      {/* Vehicle Modal */}
      <Modal
        show={showModal}
        onHide={resetForm}
        title={vehicleToEdit ? t('transport.editVehicle') : t('transport.addVehicle')}
        size="lg"
        fullscreen="sm-down"
        enforceFocus={false}
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label required">{t('transport.fields.name')}</label>
            <input
              type="text"
              className={`form-control ${formErrors?.name ? 'is-invalid' : ''}`}
              value={newVehicle.name}
              onChange={(e) => setNewVehicle({...newVehicle, name: e.target.value})}
              required
            />
            {formErrors?.name && (
              <div className="invalid-feedback">{formErrors.name[0]}</div>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label">{t('transport.fields.category')}</label>
            <Select
              value={categoryOptions.find(option => option.value === newVehicle.category) || null}
              onChange={(selectedOption) =>
                setNewVehicle({...newVehicle, category: selectedOption ? selectedOption.value : ''})
              }
              options={categoryOptions}
              isClearable
              isSearchable
              placeholder={t('transport.selectCategory')}
              noOptionsMessage={() => t('common.noOptions')}
              theme={selectTheme}
              classNamePrefix="react-select"
              className={formErrors?.category ? 'is-invalid' : ''}
            />
            {formErrors?.category && (
              <div className="invalid-feedback d-block">{formErrors.category[0]}</div>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label">{t('transport.fields.licencePlate')}</label>
            <input
              type="text"
              className={`form-control ${formErrors?.licence_plate ? 'is-invalid' : ''}`}
              value={newVehicle.licence_plate}
              onChange={(e) => setNewVehicle({...newVehicle, licence_plate: e.target.value})}
            />
            {formErrors?.licence_plate && (
              <div className="invalid-feedback">{formErrors.licence_plate[0]}</div>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label required">{t('transport.fields.description')}</label>
            <textarea
              className={`form-control ${formErrors?.description ? 'is-invalid' : ''}`}
              rows="3"
              value={newVehicle.description}
              onChange={(e) => setNewVehicle({...newVehicle, description: e.target.value})}
              required
            />
            {formErrors?.description && (
              <div className="invalid-feedback">{formErrors.description[0]}</div>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label">{t('transport.fields.nextTechnicalCheck')}</label>
            <input
              type="date"
              className={`form-control ${formErrors?.next_technical_check_date ? 'is-invalid' : ''}`}
              value={newVehicle.next_technical_check_date}
              onChange={(e) => setNewVehicle({...newVehicle, next_technical_check_date: e.target.value})}
            />
            {formErrors?.next_technical_check_date && (
              <div className="invalid-feedback">{formErrors.next_technical_check_date[0]}</div>
            )}
          </div>

          <div className="mb-3">
            <label className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={newVehicle.available}
                onChange={(e) => setNewVehicle({...newVehicle, available: e.target.checked})}
              />
              <span className="form-check-label">{t('transport.fields.available')}</span>
            </label>
          </div>

          {/* Default tariff rates */}
          <div className="row">
            <div className="col-6">
              <div className="mb-3">
                <label className="form-label">{t('transport.fields.kmRate')}</label>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    value={newVehicle.km_tariff_amount}
                    onChange={(e) => setNewVehicle({...newVehicle, km_tariff_amount: e.target.value})}
                    min="0"
                    step="any"
                    placeholder="0.00"
                  />
                  <span className="input-group-text">/ km</span>
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="mb-3">
                <label className="form-label">{t('transport.fields.flatRate')}</label>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    value={newVehicle.flat_tariff_amount}
                    onChange={(e) => setNewVehicle({...newVehicle, flat_tariff_amount: e.target.value})}
                    min="0"
                    step="any"
                    placeholder="0.00"
                  />
                  <span className="input-group-text">{t('transport.fields.flatUnit')}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="btn btn-secondary" onClick={resetForm}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={createMutation.isPending || updateMutation.isPending}>
              {(createMutation.isPending || updateMutation.isPending)
                ? <><span className="spinner-border spinner-border-sm me-2"></span>{t('common.saving')}</>
                : vehicleToEdit ? t('common.update') : t('common.create')
              }
            </button>
          </div>
        </form>

        {vehicleToEdit && (
          <>
            <hr className="my-3" />
            <VehicleTariffManagement vehicleId={vehicleToEdit.id} />
          </>
        )}
      </Modal>
    </>
  );
}

export default Transport;
